import { FlightTakeoff } from "@mui/icons-material";
import { Button, Divider, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { useMemo } from "react";

const ProcessExecutionCompletionAnimation = ({
  open,
  onClose,
  ...props
}: {
  message?: string;
  title?: string;
  open?: boolean;
  onClose?: () => void;
}) => {
  const showOverlay = open ?? false;

  const completionScreen = useMemo(() => {
    return (
      <Box
        sx={(t) => ({
          opacity: showOverlay ? 100 : 0,
          color: t.palette.getContrastText(t.palette.primary.dark),
          textAlign: "center",
          width: "35%",
          py: 1,
          transition: "0.2s ease-in-out opacity",
        })}
      >
        <Box
          sx={{
            width: "35%",
            aspectRatio: 1 / 1,
            mx: "auto",
            mb: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "visible",
            position: "relative",
          }}
        >
          {/* Splash animation */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              pointerEvents: "none",
              zIndex: 1,
              overflow: "visible",
            }}
          >
            {[...Array(10)].map((_, i) => {
              const angle = (i / 10) * 360 + 45;
              return (
                <Box
                  key={i}
                  sx={{
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    width: 24,
                    height: 24,
                    borderRadius: "50% 50% 50% 0",
                    background: `#ffffff77`,
                    // Rotate so the point faces the center
                    transform: `translate(-50%, -50%) scale(0.2) rotate(${angle}deg)`,
                    opacity: 0,
                    animation: showOverlay
                      ? `splash-pop-${i} 0.7s 0.25s ease-out forwards`
                      : "none",
                    zIndex: 1,
                    [`@keyframes splash-pop-${i}`]: {
                      "0%": {
                        transform: `translate(-50%, -50%) scale(0.2) rotate(${angle}deg)`,
                        opacity: 0.7,
                      },
                      "60%": {
                        transform: `translate(-50%, -50%) scale(1.2) translate(${Math.cos((i / 10) * 2 * Math.PI) * 60}px,${Math.sin((i / 10) * 2 * Math.PI) * 60}px) rotate(${angle}deg)`,
                        opacity: 1,
                      },
                      "100%": {
                        transform: `translate(-50%, -50%) scale(0.8) translate(${Math.cos((i / 10) * 2 * Math.PI) * 120}px,${Math.sin((i / 10) * 2 * Math.PI) * 120}px) rotate(${angle}deg)`,
                        opacity: 0,
                      },
                    },
                  }}
                />
              );
            })}
          </Box>
          {/* Plane icon */}
          <FlightTakeoff
            sx={[
              {
                width: "100%",
                height: "100%",
                zIndex: 2,
                position: "relative",
              },
              (showOverlay ?? false) && {
                animation: `plane-bounce 0.4s 0.0s ease-in`,
                "@keyframes plane-bounce": {
                  "0%": {
                    transform: "scale(4.6)",
                    dropShadow: "0px 0px 5px black",
                  },
                  "80%": {
                    transform: "scale(0.9)",
                    dropShadow: "0px 0px 1px black",
                  },
                  "100%": {
                    transform: "scale(1)",
                    dropShadow: "0px 0px 2px black",
                  },
                },
              },
            ]}
          />
        </Box>
        <Typography variant="h1">{props.title ?? `Done!`}</Typography>
        <Divider
          sx={(t) => ({
            border: `0.5px solid ${t.palette.getContrastText(t.palette.primary.dark)}`,
            my: 2,
            mx: "25%",
          })}
        />
        <Typography variant="body1">
          {props.message ??
            `Sit back and relax - we'll take it from here.\n Thank you for choosing Runway!`}
        </Typography>
        <Button
          sx={{
            mt: 2,
          }}
          variant="outlined"
          color="inherit"
          onClick={() => onClose?.()}
        >
          Close
        </Button>
      </Box>
    );
  }, [onClose, props.message, props.title, showOverlay]);

  return (
    <Box
      sx={(t) => ({
        position: "absolute",
        left: 0,
        bottom: 0,
        top: "auto",
        width: "100%",
        height: showOverlay ? "100%" : "0",
        background: t.palette.primary.dark,
        zIndex: 10,
        pointerEvents: showOverlay ? "auto" : "none",
        transition: "height 0.4s ease-in-out",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      })}
    >
      {completionScreen}
    </Box>
  );
};

export default ProcessExecutionCompletionAnimation;
